import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";

import { useNavigate } from "hooks";
import { usePickUserPermissions, useUserRoleSelector } from "store";
import { IRole, UserPermissionType } from "store/types";
import { RoleName } from "constants";

import { useClientDetail } from "queries/query-selectors/clients-query-selectors";

import { ClientPageShell } from "./ClientPageShell";
import { ClientOverviewProvider } from "context/ClientOverviewContext";
import { OverviewTab } from "./OverviewTab";
import { EntitiesTab } from "./EntitiesTab";
import { ClientWorkspaceContent } from "./ClientWorkspace/ClientWorkspaceContent";
import { ClientTasksTab } from "./ClientTasksTab";
import { ClientReportingTab } from "./ClientReportingTab";

export const ClientDetail: React.FC = () => {
  // 1. Safe hooks
  const history = useHistory();
  const navigate = useNavigate();
  const currentRole: IRole | null = useUserRoleSelector();
  const portfolioPermission = usePickUserPermissions("portfolio_analysis");
  const hasPortfolioAccess = portfolioPermission !== UserPermissionType.NO_ACCESS;

  // 2. Safe params extraction
  const params = useParams<any>() || {};
  const clientId = Number(params.client_id ?? params.id ?? 0);
  const clientIdString = String(clientId);

  // 3. Compute isTeamViewer from current role
  const isTeamViewer = currentRole?.name === RoleName.TeamViewer;

  // 4. Bulletproof query extraction
  const queryResult = useClientDetail(clientId, clientId > 0) || {};
  const { data: client, isLoading, isError, refetch } = queryResult as any;

  // 5. THE FIX: useMemo MUST be called before any early returns!
  const tabsContent = useMemo(() => {
    const content: Record<string, React.ReactNode> = {
      overview: (
        <ClientOverviewProvider>
          <OverviewTab clientId={clientIdString} isTeamViewer={isTeamViewer} />
        </ClientOverviewProvider>
      ),
      entities: <EntitiesTab clientId={clientId} roleId={currentRole?.name ?? null} />,
      tasks: (
        <ClientTasksTab
          clientId={clientId}
          clientName={client?.name || ""}
          role={currentRole?.name ?? null}
        />
      ),
      workspace: <ClientWorkspaceContent clientId={clientId} role={currentRole?.name ?? null} />
    };

    if (hasPortfolioAccess) {
      content.reporting = <ClientReportingTab clientId={clientIdString} />;
    }

    return content;
  }, [clientId, clientIdString, client?.name, currentRole?.name, hasPortfolioAccess, isTeamViewer]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { value: "overview", label: "Overview" },
      { value: "entities", label: "Entities" },
      { value: "tasks", label: "Tasks" },
    ];

    // Workspace tab: visible only for Admin, Team Member (Analyst), and Team Viewer
    if (currentRole && [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer].includes(currentRole.name as RoleName)) {
      baseTabs.push({ value: "workspace", label: "Workspace" });
    }

    if (hasPortfolioAccess) {
      baseTabs.push({ value: "reporting", label: "Reporting" });
    }

    return baseTabs;
  }, [hasPortfolioAccess, currentRole]);

  // ---- Render states (Early Returns) ----

  if (clientId <= 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No client selected
        </Typography>
        <Box sx={{ mt: 2 }}>
          <button
            onClick={() => history?.push("/main/users-management/clients")}
            style={{ padding: "8px 16px", backgroundColor: "#1976d2", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
          >
            Back to Clients
          </button>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !client) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Client not found or failed to load
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please check the URL or try again later.
        </Typography>
        <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "center" }}>
          <button
            onClick={() => history?.push("/main/users-management/clients")}
            style={{ padding: "8px 16px", backgroundColor: "#1976d2", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "8px" }}
          >
            Back to Clients
          </button>
          {refetch && (
            <button
              onClick={() => refetch()}
              style={{ padding: "8px 16px", backgroundColor: "#496682", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
            >
              Retry
            </button>
          )}
        </Box>
      </Box>
    );
  }

  // ---- Success: render the Shell ----
  return (
    <ClientPageShell
      defaultTab="overview"
      clientId={clientId}
      clientName={client.name}
      onNavigate={navigate}
      tabs={tabs}
      tabsContent={tabsContent}
    />
  );
};
